import { isApiError } from '@counsel-project/client-utils'
import BackIcon from '@mui/icons-material/ArrowBackRounded'
import { Paper } from '@mui/material'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Cookies from 'js-cookie'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import usePageTitle from '../util/usePageTitle'
import {
  AppleButton,
  ClosableDialog,
  CredentialsButton,
  GoogleButton,
  MicrosoftButton,
} from '@counsel-project/components'
import { REDIRECT_URI, MS_REDIRECT_URI, APPLE_REDIRECT_URI } from '../components/auth/util'
import { authRequest } from '../util/api/auth-api'
import PageContainer from '../components/layout/PageContainer'
import {
  loginWithEmail,
  loginWithEmail2FA,
  loginWithGoogle,
  loginWithPhone2FA,
} from '../util/auth/login'
import useUser from '../util/auth/useUser'
import log from '../util/logging'
import { acceptPrivacyPolicy } from '../util/privacy-policy'
import { getCNAIUrl } from '../util/cnaiUrl'
import { setStore } from '../util/store/auth'
import { logLogin, useViewContent } from '../util/tracking'

export const attemptJSONParse = (string: string): { [key: string]: unknown } | null => {
  try {
    return JSON.parse(string)
  } catch {
    return null
  }
}

type MFADialogProps = {
  mfaMethod: 'phone' | 'email' | null
  userId: string | null
  onClose: () => void
}

const MFADialog = ({ mfaMethod, userId, onClose }: MFADialogProps) => {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleVerify = useCallback(async () => {
    try {
      if (!code) return
      if (!userId) return
      if (!mfaMethod) return

      setLoading(true)

      if (mfaMethod === 'phone') {
        await loginWithPhone2FA({
          userId,
          code,
        })
      } else {
        await loginWithEmail2FA({
          userId,
          code,
        })
      }

      log.info('Logged in')

      logLogin('mfa')
    } catch (err) {
      if (isApiError(err)) {
        if (err.msg !== 'Invalid credentials') {
          log.error(err.msg)
        }
        setError(err.msg)
      }
      log.warn(err)
    } finally {
      setLoading(false)
    }
  }, [code, userId, mfaMethod])

  useEffect(() => {
    if (!code) return
    if (code.length !== 6) return
    const timeout = setTimeout(() => void handleVerify(), 100)
    return () => clearTimeout(timeout)
  }, [code, handleVerify])

  const mfaMethodText = useMemo(() => {
    if (mfaMethod === 'phone') {
      return 'Please enter the verification code we have sent to your phone'
    } else if (mfaMethod === 'email') {
      return 'Please enter the verification code we have sent to your email'
    }
    return ''
  }, [mfaMethod])

  return (
    <ClosableDialog open={!!mfaMethod} onClose={onClose} title="Verification Code">
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12}>
          <Collapse in={Boolean(error)}>
            <Alert severity="error">{error}</Alert>
          </Collapse>
        </Grid>
        <Grid item xs={12}>
          <Typography>{mfaMethodText}</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            placeholder="Code"
            aria-label="code"
            fullWidth
            onChange={(e) => setCode(e.target.value)}
            value={code}
            disabled={loading}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <Button fullWidth onClick={() => void handleVerify()} disabled={loading}>
            Verify
          </Button>
        </Grid>
      </Grid>
    </ClosableDialog>
  )
}

const Login = () => {
  useViewContent('Login')
  usePageTitle('Login')

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState<boolean>(false)
  const [user] = useUser()
  const [manualLogin, setManualLogin] = useState(false)
  const [mfaMethod, setMFAMethod] = useState<'phone' | 'email' | null>(null)
  const [mfaUserId, setMFAUserId] = useState<string | null>(null)

  const state = searchParams.get('state')
  let page = attemptJSONParse(decodeURIComponent(state ?? ''))?.page
  if (page === '/login' || page === '/register') {
    page = undefined
  }
  const handleChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value)
    },
    [setEmail]
  )

  const handleChangePassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value)
    },
    [setPassword]
  )

  const createNavHandler = useCallback(
    (path: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      navigate(path)
    },
    [navigate]
  )

  const handleLogin = useCallback(async () => {
    try {
      if (!email) return
      if (!password) return

      setStore({
        user: null,
      })

      Cookies.remove('expiresAt')

      setLoading(true)

      const data = await loginWithEmail({
        email,
        password,
      })

      if ('twoFactorAuth' in data) {
        setMFAMethod(data.twoFactorAuth)
        setMFAUserId(data.userId)
        return
      }

      log.info('Logged in')

      logLogin('email')
    } catch (err) {
      if (isApiError(err)) {
        if (err.msg !== 'Invalid credentials') {
          log.error(err.msg)
        }
        setError(err.msg)
      }
      log.warn(err)
    } finally {
      setLoading(false)
    }
  }, [email, password])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      handleLogin().catch(log.error)
    },
    [handleLogin]
  )

  const handleGoogleLogin = useCallback(async () => {
    try {
      const code = searchParams.get('code')
      if (!code) return

      setLoading(true)

      setStore({
        user: null,
      })

      Cookies.remove('expiresAt')

      logLogin('google')

      await loginWithGoogle({
        code,
        redirectUri: REDIRECT_URI,
      })

      acceptPrivacyPolicy()
    } catch (err) {
      if (isApiError(err)) {
        log.error(err.message)
        toast.error("We couldn't sign you in with Google. Please try again.")
      } else {
        toast.error("We couldn't sign you in with Google. Please try again.")
        log.error(err)
      }

      navigate('/login')
    } finally {
      setLoading(false)
    }
  }, [navigate, searchParams])

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleGoogleLogin().catch(log.error)
    }, 100)
    return () => clearTimeout(timeout)
  }, [handleGoogleLogin])

  const handleSSOClick = useCallback(
    async (method: 'google' | 'microsoft' | 'apple') => {
      try {
        setLoading(true)

        const ssoState = JSON.stringify({ page: page || '/' })
        let authUrl = ''

        if (method === 'google') {
          const data = await authRequest.user.google.getAuthUrl({
            redirectUri: REDIRECT_URI,
            state: ssoState,
          })
          authUrl = data.url
        } else if (method === 'microsoft') {
          const data = await authRequest.user.microsoft.getAuthUrl({
            redirectUri: MS_REDIRECT_URI,
            state: ssoState,
          })
          authUrl = data.url
        } else {
          const appleState = crypto.randomUUID()
          Cookies.set('appleState', appleState, { secure: !import.meta.env.DEV })
          if (page) {
            Cookies.set('redirectPage', page as string, { secure: !import.meta.env.DEV })
          }
          const data = await authRequest.user.apple.getAuthUrl({
            redirectUri: APPLE_REDIRECT_URI,
            responseMode: 'form_post',
            state: appleState,
            scope: 'name email',
          })
          authUrl = data.url
        }

        globalThis.location.href = authUrl
      } catch (err) {
        log.error(err)
        toast.error('Unable to start sign-in. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [page]
  )

  useEffect(() => {
    const autoLogin = searchParams.get('autoLogin')
    if (autoLogin === 'google' || autoLogin === 'microsoft' || autoLogin === 'apple') {
      handleSSOClick(autoLogin).catch(log.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const afterLogin = useCallback(() => {
    if (!user) return
    navigate(page ?? '/')
  }, [user, page, navigate])

  useEffect(() => {
    const timeout = setTimeout(() => {
      afterLogin()
    }, 100)
    return () => clearTimeout(timeout)
  }, [afterLogin])

  const LoginComponents = useMemo(() => {
    if (manualLogin) {
      return (
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Grid container padding={2} alignItems="center" spacing={2}>
            <Grid item>
              <IconButton onClick={() => setManualLogin(false)} data-qa-id="login-back-button">
                <BackIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Typography variant="h5">Login</Typography>
            </Grid>
          </Grid>
          <FormControl component={'form'} sx={{ width: '100%' }} onSubmit={handleSubmit}>
            <Grid container justifyContent="center" direction="column" padding={2} spacing={2}>
              <Grid item xs={12}>
                <Collapse in={Boolean(error)}>
                  <Alert severity="error">{error}</Alert>
                </Collapse>
              </Grid>
              <Grid item>
                <TextField
                  placeholder="E-mail"
                  type="email"
                  aria-label="email"
                  fullWidth
                  onChange={handleChangeEmail}
                  value={email}
                  disabled={loading}
                  inputProps={{ 'data-qa-id': 'login-email-input' }}
                />
              </Grid>
              <Grid item>
                <TextField
                  placeholder="Password"
                  type="password"
                  aria-label="password"
                  fullWidth
                  onChange={handleChangePassword}
                  value={password}
                  disabled={loading}
                  inputProps={{ 'data-qa-id': 'login-password-input' }}
                />
              </Grid>
              <Grid item>
                <Button fullWidth type="submit" disabled={loading} data-qa-id="login-continue-button">
                  Continue
                </Button>
              </Grid>
            </Grid>

            <Grid container justifyContent="center" paddingX={2} paddingBottom={2}>
              <Grid item>
                <Typography>
                  Forgot your password? Click{' '}
                  <Link
                    onClick={createNavHandler('/reset-password')}
                    href="/reset-password"
                    data-qa-id="login-forgot-password-link"
                  >
                    here
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </FormControl>
        </Paper>
      )
    } else {
      return (
        <Paper
          elevation={0}
          sx={{ p: 0, borderRadius: { xs: 0, sm: 1 }, mb: 2, background: 'transparent' }}
        >
          <Grid container justifyContent="center" padding={2}>
            <Grid item>
              <Typography variant="h5">Sign into Clinical Notes AI</Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <GoogleButton
                fullWidth
                disabled={loading}
                onClick={() => void handleSSOClick('google')}
              />
            </Grid>
            <Grid item xs={12}>
              <MicrosoftButton
                fullWidth
                disabled={loading}
                onClick={() => void handleSSOClick('microsoft')}
              />
            </Grid>
            <Grid item xs={12}>
              <AppleButton
                fullWidth
                disabled={loading}
                onClick={() => void handleSSOClick('apple')}
              />
            </Grid>
            <Grid item xs={12}>
              <CredentialsButton fullWidth onClick={() => setManualLogin(true)} />
            </Grid>
          </Grid>
        </Paper>
      )
    }
  }, [
    createNavHandler,
    handleSubmit,
    handleChangeEmail,
    handleChangePassword,
    handleSSOClick,
    loading,
    error,
    manualLogin,
    email,
    password,
  ])

  return (
    <PageContainer>
      <MFADialog mfaMethod={mfaMethod} userId={mfaUserId} onClose={() => setMFAMethod(null)} />
      {searchParams.get('code') ? (
        <Grid container justifyContent="center" spacing={4} direction="column" alignItems="center">
          {!!error && (
            <Grid item xs={12} sm={8} md={5} lg={4}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : (
        LoginComponents
      )}
      <Grid container justifyContent="center" paddingX={2} paddingTop={2}>
        <Grid item>
          <Typography>
            Don&apos;t have an account? Get started{' '}
            <Link
              href={getCNAIUrl('/pricing-select')}
              target="_blank"
              rel="noopener noreferrer"
              data-qa-id="login-create-account-link"
            >
              here
            </Link>
          </Typography>
        </Grid>
      </Grid>
    </PageContainer>
  )
}

export default Login
